import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabaseRpc } from '@/lib/supabaseClient'

export type PluginSettings = {
  maxWidth?: number
  maxHeight?: number
  format?: 'original' | 'webp' | 'jpeg' | 'png'
  quality?: number
}

export type ExifExtractorSettings = {
  fieldGroups?: {
    location?: boolean
    dateTime?: boolean
    cameraInfo?: boolean
    shootingSettings?: boolean
  }
}

export type UiDropPlugin = {
  id: string
  dropId: string
  pluginId: string
  settings: PluginSettings
  enabled: boolean
}

type DropPluginRow = {
  id: string
  drop_id?: string
  dropId?: string
  plugin_id?: string
  pluginId?: string
  settings?: Record<string, unknown> | null
  enabled?: boolean | null
}

function toUiDropPlugin(row: DropPluginRow): UiDropPlugin {
  return {
    id: row.id,
    dropId: row.drop_id ?? row.dropId ?? '',
    pluginId: row.plugin_id ?? row.pluginId ?? '',
    settings: (row.settings ?? {}) as PluginSettings,
    enabled: row.enabled ?? true,
  }
}

const PRO_PLUGIN_ID = 'com.photodrop.pro-image-processor'
const EXIF_PLUGIN_ID = 'com.photodrop.exif-extractor'

export function useDropPluginsQuery(dropId: string | undefined) {
  const query = useQuery({
    queryKey: ['drop-plugins', dropId],
    queryFn: () => supabaseRpc<DropPluginRow[]>('list_drop_plugins', { p_drop_id: dropId }),
    enabled: !!dropId,
  })

  const plugins = (query.data || []).map(toUiDropPlugin)
  const proPlugin = plugins.find((p) => p.pluginId === PRO_PLUGIN_ID)
  const exifPlugin = plugins.find((p) => p.pluginId === EXIF_PLUGIN_ID)

  return { ...query, plugins, proPlugin, exifPlugin }
}

export function useUpsertDropPluginMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { dropId: string; pluginId: string; settings: Record<string, unknown> }) =>
      supabaseRpc('upsert_drop_plugin', {
        p_drop_id: vars.dropId,
        p_plugin_id: vars.pluginId,
        p_settings: vars.settings,
      }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['drop-plugins', vars.dropId] })
    },
  })
}

export function useDeleteDropPluginMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { dropId: string; pluginId: string }) =>
      supabaseRpc('delete_drop_plugin', {
        p_drop_id: vars.dropId,
        p_plugin_id: vars.pluginId,
      }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['drop-plugins', vars.dropId] })
    },
  })
}
